import { useEffect, useRef } from 'react'
import maplibregl from 'maplibre-gl'
import { MAP_COLORS } from '../config/mapConfig'

export interface LocationMarkerLocation {
	id: string
	assetName: string
	address?: string
	city?: string
	stateProvince?: string
	latitude: number
	longitude: number
	image?: string
}

export interface LocationMarkerProps {
	map: maplibregl.Map | null
	location: LocationMarkerLocation
	color?: string
	label?: string
	/**
	 * Draw this marker as an approximate placement (dashed border, faded)
	 * rather than a surveyed position. Used when the pin is the centre of a
	 * city or postal code because the address could not be geocoded.
	 */
	isApproximate?: boolean
	/** What the approximate pin is the centre of, shown in the popup. */
	approximateLabel?: string
	onViewDetails?: (locationId: string) => void
	/**
	 * Route the user away to the location's own screen. When supplied, the
	 * popup carries a second "Goto Location" button beside "View Details".
	 */
	onGotoLocation?: (locationId: string) => void
}

function escapeHtml(value: string): string {
	return value
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}

function buildMarkerElement(
	color: string,
	label?: string,
	isApproximate = false,
): HTMLDivElement {
	const el = document.createElement('div')
	// NOTE: do not set `transform` (inline or via hover) on this element —
	// MapLibre uses the marker root's inline `transform` to position it on
	// the map, and any override wipes the translate and snaps the marker
	// to (0,0) of its container.
	//
	// An approximate marker is dashed and semi-transparent so it reads as
	// "somewhere around here" at a glance, without changing its size or
	// position and so without disturbing the letter sequence.
	el.style.cssText = `
		width:36px;height:36px;border-radius:50%;
		background:${color};color:#ffffff;
		display:flex;align-items:center;justify-content:center;
		font-weight:700;font-size:15px;
		font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
		box-shadow:0 2px 6px rgba(0,0,0,0.3);
		border:${isApproximate ? '2px dashed #ffffff' : '2px solid #ffffff'};
		opacity:${isApproximate ? '0.72' : '1'};
		cursor:pointer;
	`
	if (label) {
		el.textContent = label
	} else {
		const dot = document.createElement('div')
		dot.style.cssText = 'width:10px;height:10px;border-radius:50%;background:#ffffff;'
		el.appendChild(dot)
	}
	return el
}

function buildPopupHTML(
	location: LocationMarkerLocation,
	isApproximate = false,
	approximateLabel?: string,
	showGoto = false,
): string {
	const addressLine = [location.address, location.city, location.stateProvince]
		.filter(Boolean)
		.join(', ')
	// Say so explicitly. A pin that looks placed but is really a city centre
	// is the kind of thing someone would otherwise measure a decision against.
	const approximateRow = isApproximate
		? `<div style="margin-top:6px;padding:4px 6px;border-radius:4px;background:#fffbeb;border:1px solid #fde68a;color:#92400e;font-size:11px;">
				Approximate location${approximateLabel ? ` — centre of ${escapeHtml(approximateLabel)}` : ''}
			</div>`
		: ''
	const imageBlock = location.image
		? `<img src="${escapeHtml(location.image)}" alt="" style="width:48px;height:48px;border-radius:8px;object-fit:cover;flex-shrink:0;" />`
		: ''
	const headerRow = imageBlock
		? `<div style="display:flex;gap:10px;align-items:center;margin-bottom:6px;">
				${imageBlock}
				<div style="font-weight:600;font-size:14px;color:#171717;line-height:1.3;">${escapeHtml(location.assetName)}</div>
			</div>`
		: `<div style="font-weight:600;font-size:14px;color:#171717;margin-bottom:6px;">${escapeHtml(location.assetName)}</div>`

	const addressRow = addressLine
		? `<div style="color:#6b7280;font-size:12px;margin-top:2px;">📍 ${escapeHtml(addressLine)}</div>`
		: ''

	// The two actions differ in where they leave you: "Goto Location" leaves for
	// the location's own screen, "View Details" opens it beside the map. They
	// stack rather than sit side by side, so neither label has to wrap in a
	// popup this narrow. The first is only drawn when the caller has somewhere
	// to route to.
	const gotoButton = showGoto
		? `<button data-marker-action="goto-location"
				style="width:100%;padding:6px 10px;background:#ffffff;color:${MAP_COLORS.primary};border:1px solid ${MAP_COLORS.primary};border-radius:6px;font-size:12px;font-weight:500;cursor:pointer;">
				Goto Location
			</button>`
		: ''

	return `
		<div style="min-width:200px;">
			${headerRow}
			<div style="border-top:1px solid #e5e7eb;margin:6px 0;"></div>
			${addressRow}
			${approximateRow}
			<div style="display:flex;flex-direction:column;gap:6px;margin-top:10px;">
				${gotoButton}
				<button data-marker-action="view-details"
					style="width:100%;padding:6px 10px;background:${MAP_COLORS.primary};color:white;border:none;border-radius:6px;font-size:12px;font-weight:500;cursor:pointer;">
					View Details →
				</button>
			</div>
		</div>
	`
}

export function LocationMarker({
	map,
	location,
	color = MAP_COLORS.primary,
	label,
	isApproximate = false,
	approximateLabel,
	onViewDetails,
	onGotoLocation,
}: LocationMarkerProps) {
	const markerRef = useRef<maplibregl.Marker | null>(null)

	useEffect(() => {
		if (!map) return
		if (location.latitude == null || location.longitude == null) return

		const el = buildMarkerElement(color, label, isApproximate)
		const popup = new maplibregl.Popup({ offset: 24, closeButton: true }).setHTML(
			buildPopupHTML(location, isApproximate, approximateLabel, Boolean(onGotoLocation)),
		)

		popup.on('open', () => {
			const popupEl = popup.getElement()
			const viewButton = popupEl?.querySelector<HTMLButtonElement>(
				'[data-marker-action="view-details"]',
			)
			viewButton?.addEventListener('click', () => {
				onViewDetails?.(location.id)
				popup.remove()
			})
			const gotoButton = popupEl?.querySelector<HTMLButtonElement>(
				'[data-marker-action="goto-location"]',
			)
			gotoButton?.addEventListener('click', () => {
				onGotoLocation?.(location.id)
				popup.remove()
			})
		})

		const marker = new maplibregl.Marker({ element: el })
			.setLngLat([location.longitude, location.latitude])
			.setPopup(popup)
			.addTo(map)

		markerRef.current = marker

		return () => {
			marker.remove()
			markerRef.current = null
		}
	}, [map, location, color, label, isApproximate, approximateLabel, onViewDetails, onGotoLocation])

	return null
}
